import { type CatalogItem, Requisition, RequisitionItem, type User } from '@kalos/kalos-rpc';
import { addWeeks, format } from 'date-fns';
import { z } from 'zod';

import { type ToolSet } from '../../../hooks/react-query/useToolSetQuery';

/**
 * The New Tool Request's rules and request, shared by desktop's `RequisitionForm` and the phone's
 * `NewToolRequestPhone`, so both send the same requisition. Moved here from `RequisitionForm`
 * unchanged.
 */
export const requisitionFormSchema = z
  .object({
    tools: z
      .array(
        z.object({
          categoryId: z.number().optional(),
          catalogItemId: z.number().optional(),
          quantity: z.number().min(1, { message: 'Quantity must be at least 1' }),
          notes: z.string().optional(),
        }),
      )
      .min(1, { message: 'At least one tool must be selected' }),
    deliveryMethod: z
      .string({
        required_error: 'Please select a delivery method',
      })
      .min(1, { message: 'Please select a delivery method' }),
    confirmedDeliveryAddress: z
      .object({
        placeId: z.string(),
        name: z.string(),
        address: z.string(),
      })
      .optional(),
    requestDate: z.date({
      required_error: 'Please select when you need the tool',
    }),
    selectedTechnicianId: z.number().optional(),
    internalPurchase: z.boolean().default(false),
    /** A ToolFundAdmin requesting for someone else charges that person instead of themselves. */
    requesteePays: z.boolean().optional(),
    purchaseComments: z.string().optional(),
    toolSetDepartmentId: z.number().optional(),
    toolSetId: z.number().optional(),
  })
  .refine(
    (data) => {
      if (data.deliveryMethod === 'other') {
        return !!data.confirmedDeliveryAddress?.placeId;
      }
      return true;
    },
    {
      message: 'Please select a valid delivery address from the dropdown',
      path: ['confirmedDeliveryAddress'],
    },
  )
  .refine(
    (data) => {
      return data.tools.some((tool) => tool.catalogItemId);
    },
    {
      message: 'At least one tool must be selected',
      path: ['tools'],
    },
  );

export type RequisitionFormData = z.infer<typeof requisitionFormSchema>;
export type RequisitionTool = RequisitionFormData['tools'][number];

/** An empty tool line: desktop shows one to pick into. */
export const makeEmptyRequisitionTool = (): RequisitionTool => ({
  categoryId: undefined,
  catalogItemId: undefined,
  quantity: 1,
  notes: '',
});

/** Desktop's starting values: one empty tool line, warehouse pickup, needed two weeks from now. */
export const makeRequisitionFormDefaultValues = (): RequisitionFormData => ({
  tools: [makeEmptyRequisitionTool()],
  deliveryMethod: 'warehouse',
  requestDate: addWeeks(new Date(), 2),
  internalPurchase: false,
  requesteePays: undefined,
  purchaseComments: '',
  selectedTechnicianId: undefined,
});

/**
 * Who a request is charged to: the requester, unless a ToolFundAdmin requesting for someone else
 * chose to charge that person. The backend only accepts another payer from a ToolFundAdmin.
 */
export const requisitionPayerId = ({
  requesterId,
  requesteeId,
  requesteePays,
  isToolFundAdmin,
}: {
  requesterId: number;
  requesteeId: number;
  requesteePays: boolean | undefined;
  isToolFundAdmin: boolean;
}) => (isToolFundAdmin && requesteePays === true && requesteeId > 0 ? requesteeId : requesterId);

/** A catalog tool as desktop's tool picker lists it. */
export type CatalogItemOption = { label: string; value: string; original: CatalogItem };

/** Desktop's category filter on the tool list: every tool when no category is chosen. */
export const filterToolsByCategory = (tools: CatalogItem[], categoryId: number | undefined) =>
  categoryId ? tools.filter((item) => item.categoryId === categoryId) : tools;

export const toCatalogItemOptions = (tools: CatalogItem[]): CatalogItemOption[] =>
  tools.map((item) => ({
    label: `${item.name}-${item.brand} ($${item.cost.toFixed(2)})`,
    value: item.id.toString(),
    original: item,
  }));

export const findCatalogItem = (
  catalogItems: CatalogItemOption[],
  catalogItemId: number | undefined,
) => catalogItems.find((item) => Number(item.value) === catalogItemId)?.original;

/** The cost of every tool line: price times quantity, skipping lines with no tool. */
export const requisitionTotalCost = (tools: RequisitionTool[], catalogItems: CatalogItemOption[]) =>
  tools.reduce((total, tool) => {
    const item = findCatalogItem(catalogItems, tool.catalogItemId);
    return total + (item ? item.cost * tool.quantity : 0);
  }, 0);

/** What would be left after this request: the available balance less the tool cost. */
export const toolFundNewBalance = (availableBalance: number, totalCost: number) =>
  availableBalance - totalCost;

export const exceedsToolFundBalance = (availableBalance: number, totalCost: number) =>
  availableBalance < totalCost;

/** Why the request can't be sent yet. Desktop disables Submit exactly when this isn't empty. */
export type RequisitionSubmitBlocker =
  | 'noTools'
  | 'exceedsBalance'
  | 'noDeliveryAddress'
  | 'noPayerChosen';

export const getRequisitionSubmitBlockers = ({
  tools,
  exceedsBalance,
  deliveryMethod,
  confirmedDeliveryAddress,
  needsPayerChoice = false,
}: {
  tools: RequisitionTool[];
  exceedsBalance: boolean;
  deliveryMethod: string;
  confirmedDeliveryAddress: RequisitionFormData['confirmedDeliveryAddress'];
  /** A ToolFundAdmin requesting for someone else hasn't chosen who pays. */
  needsPayerChoice?: boolean;
}): RequisitionSubmitBlocker[] => {
  const blockers: RequisitionSubmitBlocker[] = [];
  if (!tools.some((tool) => tool.catalogItemId)) blockers.push('noTools');
  if (exceedsBalance) blockers.push('exceedsBalance');
  if (deliveryMethod === 'other' && !confirmedDeliveryAddress?.placeId) {
    blockers.push('noDeliveryAddress');
  }
  if (needsPayerChoice) blockers.push('noPayerChosen');
  return blockers;
};

/** The street address desktop shows for home delivery. */
export const formatHomeAddress = (user: Pick<User, 'address' | 'city' | 'state' | 'zip'>) =>
  `${user.address}, ${user.city}, ${user.state} ${user.zip}`;

/** The warehouse address desktop shows under Warehouse Pickup. */
export const WAREHOUSE_DISPLAY_ADDRESS = '236 Hatteras Ave, Clermont Fl, 34711';

/**
 * The `requestedDeliveryLocation` desktop sends. The trailing space after the warehouse address
 * and its different spelling from the one shown are desktop's (ClickUp 86bcdca47 asks about them);
 * kept byte for byte so both send the same thing.
 */
export const formatDeliveryAddress = (
  method: string,
  customAddress: { name: string; address: string } | undefined,
  homeUser: Pick<User, 'address' | 'city' | 'state' | 'zip'>,
) => {
  switch (method) {
    case 'other':
      if (!customAddress) return '';
      return `(${customAddress.name}), ${customAddress.address}`;

    case 'home':
      // Use the target technician's address (displayUser), not the logged-in user's address
      return `(Home Address), ${formatHomeAddress(homeUser)}`;

    case 'warehouse':
      return '(Warehouse), 236 Hatteras Ave, Clermont, FL 34711 ';

    default:
      return '';
  }
};

/**
 * Desktop's requisition: one item per chosen tool, for the chosen technician or else `userId`.
 * Returns null (and logs, as desktop does) when no tool line has a catalog tool.
 */
export const buildRequisition = ({
  data,
  userId,
  catalogItems,
  deliveryAddress,
  payerId,
}: {
  data: RequisitionFormData;
  userId: number;
  catalogItems: CatalogItemOption[];
  deliveryAddress: string;
  payerId: number;
}) => {
  const requisitionItems = data.tools
    .filter((tool) => tool.catalogItemId)
    .map((tool) => {
      const catalogItem = findCatalogItem(catalogItems, tool.catalogItemId);

      if (!catalogItem) {
        console.error('Catalog item not found for tool:', tool);
        return null;
      }

      return RequisitionItem.create({
        catalogItemId: tool.catalogItemId,
        quantityRequested: tool.quantity,
        estimatedCost: (catalogItem.cost || 0) * tool.quantity,
        customItemName: catalogItem.name || '',
        customItemDescription: data.purchaseComments || '',
        status: 1,
        requestedDeliveryLocation: deliveryAddress,
        internalPurchase: data.internalPurchase,
      });
    })
    .filter((item): item is RequisitionItem => item !== null); // Remove any null items

  // Ensure we have at least one valid requisition item
  if (requisitionItems.length === 0) {
    console.error('No valid requisition items to submit');
    return null;
  }

  return Requisition.create({
    userId: data.selectedTechnicianId || userId,
    statusId: 1,
    requisitionTypeId: 1,
    items: requisitionItems,
    requestDate: format(data.requestDate, 'yyyy-MM-dd HH:mm:ss'),
    chargedToEmpId: payerId,
    // createdBy is set by backend from authenticated user - do not set from frontend
  });
};

/**
 * Picking a tool: a tool already on the request goes up by one (and the empty line goes), as
 * desktop's tool picker does; otherwise it fills the empty line, or is added.
 */
export const addCatalogItemToTools = (
  tools: RequisitionTool[],
  catalogItemId: number,
): RequisitionTool[] => {
  const existingToolIndex = tools.findIndex((tool) => tool.catalogItemId === catalogItemId);
  if (existingToolIndex !== -1) {
    return tools
      .map((tool, index) =>
        index === existingToolIndex ? { ...tool, quantity: tool.quantity + 1 } : tool,
      )
      .filter((tool) => tool.catalogItemId);
  }
  const emptyToolIndex = tools.findIndex((tool) => !tool.catalogItemId);
  if (emptyToolIndex !== -1) {
    return tools.map((tool, index) =>
      index === emptyToolIndex ? { ...tool, catalogItemId, quantity: 1 } : tool,
    );
  }
  return [...tools, { ...makeEmptyRequisitionTool(), catalogItemId }];
};

/** Removing a tool line; with none left, an empty line stays to pick into (desktop's rule). */
export const removeToolAt = (tools: RequisitionTool[], index: number): RequisitionTool[] => {
  const updatedTools = tools.filter((_, i) => i !== index);

  // If no tools remain, add an empty tool entry so user can select a new one
  if (updatedTools.length === 0) {
    updatedTools.push(makeEmptyRequisitionTool());
  }

  return updatedTools;
};

/**
 * Adding a tool set: its active tools join the request, a tool already on it going up by the
 * set's quantity, and empty lines dropped. Desktop's merge, moved here unchanged.
 */
export const mergeToolSetIntoTools = (
  currentTools: RequisitionTool[],
  selectedToolSet: ToolSet,
): RequisitionTool[] => {
  const toolSetItems =
    selectedToolSet.items
      ?.filter((item) => item.isActive)
      .map((item) => ({
        catalogItemId: item.catalogItemId,
        quantity: item.quantity || 1,
      })) || [];

  // Remove any empty tool entries first
  const filteredTools = currentTools.filter((tool) => tool.catalogItemId);

  // Merge tool set items with existing tools, incrementing quantities for duplicates
  const mergedTools = [...filteredTools];

  toolSetItems.forEach((toolSetItem) => {
    const existingToolIndex = mergedTools.findIndex(
      (tool) => tool.catalogItemId === toolSetItem.catalogItemId,
    );

    if (existingToolIndex !== -1) {
      // Tool already exists - increment quantity
      mergedTools[existingToolIndex] = {
        ...mergedTools[existingToolIndex],
        quantity: mergedTools[existingToolIndex].quantity + toolSetItem.quantity,
      };
    } else {
      // Tool doesn't exist - add it as a new entry
      mergedTools.push({
        categoryId: undefined,
        catalogItemId: toolSetItem.catalogItemId,
        quantity: toolSetItem.quantity,
        notes: '',
      });
    }
  });

  return mergedTools;
};

/** Desktop's wording when approved tools haven't been marked received. */
export const UNRECEIVED_TOOLS_COPY = {
  title: 'You have not received tools that are approved',
  body: 'Please confirm that you have received all approved tools before requesting a new tool.',
} as const;

/** Desktop's "Insufficient Balance" message, verbatim. */
export const INSUFFICIENT_BALANCE_COPY = {
  title: 'Insufficient Balance',
  body: 'Error: The cost of this tool exceeds your current balance. Please wait for your next allotment or contact your manager if this tool is critical to completing a current job.',
} as const;
